import type { Dict } from './types';

/**
 * English dictionary.
 *
 * Typed as `Dict`, which is derived from `la.ts` — a missing or misspelled key
 * fails the build instead of rendering blank at the counter (docs/spec.md §8.1).
 */
export const en: Dict = {
  clinicName: 'Seenaly Clinic',
  clinicKicker: 'Clinic management',
  clinicSubtitle: 'OB-GYN Specialist / General',
  clinicPhone: 'Tel: 020 28265455',

  breadcrumbRoot: 'Clinic system',
  searchPlaceholder: 'Search patients, appointments, invoices...',
  langLabel: 'ລາວ',

  menuMain: 'Main',
  menuReports: 'Reports',
  reportRevenue: 'Revenue report',
  reportPatients: 'Patient report',

  helpTitle: 'Need help?',
  // Corrected from the prototype, which showed 021 456 789 here.
  helpBody: 'Contact support at 020 28265455',

  greetingMorning: 'Good morning',
  greetingAfternoon: 'Good afternoon',
  greetingEvening: 'Good evening',
  greetingSub: "Today's activity at Seenaly Clinic",

  refresh: 'Refresh',
  viewList: 'List',
  viewCalendar: 'Calendar',
  viewAll: 'View all',

  nav: {
    dashboard: 'Dashboard',
    appointments: 'Appointments',
    patients: 'Patients',
    billing: 'Billing',
    inventory: 'Inventory',
    medSale: 'Medicine sale',
    lab: 'Lab results',
    revenue: 'Revenue report',
    stockReport: 'Stock report',
    profitReport: 'Drug profit report',
    settings: 'Settings',
  },

  headings: {
    dashboard: 'Dashboard',
    appointments: 'All appointments',
    patients: 'All patients',
    billing: 'Billing',
    inventory: 'Inventory & supplies',
    medSale: 'Medicine sale',
    lab: 'Lab results',
    revenue: 'Revenue report',
    stockReport: 'Stock report',
    profitReport: 'Drug profit report',
    settings: 'Settings',
  },

  kpi: {
    today: "Today's appointments",
    patients: 'Total patients',
    revenue: 'Revenue this month',
    myRevenue: 'My total this month',
    myLabTests: 'My lab tests this month',
    lowstock: 'Low stock items',
  },

  chartTitle: 'Last 14 days',
  chartSub: 'Appointments vs visits (people)',
  legendA: 'Appointments',
  legendB: 'Visits',
  donutTitle: 'Revenue by service',
  lowStockTitle: 'Low stock',
  lowStockSub: 'Items to reorder',
  activityTitle: 'Recent activity',
  todaySchedule: "Today's schedule",
  recentPatients: 'Recent patients',

  revenueSub: 'Overview of the last 6 months',
  exportReport: 'Export report',
  avgPerPatient: 'Avg / patient',
  monthlyRevenueTitle: 'Monthly revenue',
  monthlyRevenueSub: 'LAK, last 6 months',
  topInvoicesTitle: 'Recent invoices',
  outstanding: 'Outstanding',
  totalRevenue: 'Total revenue',
  paidCount: 'Paid invoices',

  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],

  table: {
    patient: 'Patient',
    doctor: 'Doctor',
    time: 'Time',
    status: 'Status',
    age: 'Age',
    phone: 'Phone',
    lastVisit: 'Last visit',
    service: 'Service',
    amount: 'Amount',
    date: 'Date',
    item: 'Item',
    category: 'Category',
    stock: 'Stock',
    unitPrice: 'Unit price',
    invoice: 'Invoice #',
    actions: '',
    source: 'Source',
  },

  status: {
    confirmed: 'Confirmed',
    pending: 'Pending',
    arrived: 'Arrived',
    done: 'Done',
    cancelled: 'Cancelled',
    noShow: 'No show',
    paid: 'Paid',
    partial: 'Partially paid',
    unpaid: 'Unpaid',
    draft: 'Draft',
    void: 'Void',
    low: 'Low stock',
    ok: 'In stock',
    active: 'Active',
    followUp: 'Follow-up',
    new: 'New',
  },

  btn: {
    newAppt: 'New appointment',
    newApptShort: 'New appointment',
    newPatient: 'New patient',
    newInvoice: 'New invoice',
    newLab: 'New lab result',
    newSale: 'New sale',
    newUser: 'New user',
    addItem: 'Add item',
    stockIn: 'Stock in',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    signIn: 'Sign in',
    logout: 'Log out',
    printReceipt: 'Print receipt',
    printNow: 'Print',
    exportExcel: 'Export to Excel',
    resetPw: 'Reset password',
    retry: 'Retry',
    scan: 'Scan barcode',
    confirm: 'Confirm',
  },

  login: {
    title: 'Sign in',
    subtitle: 'Seenaly Clinic management system',
    username: 'Username',
    password: 'Password',
    forgot: 'Forgot password?',
    or: 'or',
    usePasskey: 'Sign in with fingerprint',
    passkeyHint: 'Register this device first under Settings → Personal',
  },

  passkeys: {
    title: 'Fingerprint sign-in',
    hint: 'Register this device to sign in with your fingerprint or face instead of typing a password. Your password keeps working either way.',
    registerThisDevice: 'Register this device',
    deviceLabel: 'Device name',
    deviceLabelHint: 'e.g. Reception PC, Doctor phone',
    added: 'Registered',
    lastUsed: 'Last used',
    unnamed: 'Unnamed device',
    registered: 'Device registered',
    none: 'No devices registered yet',
    noneSub: 'Once registered you can sign in with a fingerprint straight away',
    unsupported:
      'This device or browser cannot do fingerprint sign-in (it needs HTTPS and a fingerprint/face sensor)',
  },

  settings: {
    profile: 'Profile',
    clinicInfo: 'Clinic info',
    language: 'Language',
    theme: 'Color theme',
    mode: 'Appearance',
    users: 'System users',
  },

  themes: {
    rose: 'Rose',
    red: 'Red',
    berry: 'Berry',
  },

  modeLight: 'Light',
  modeDark: 'Dark',

  roles: {
    admin: 'Administrator',
    doctor: 'Doctor',
    reception: 'Receptionist',
    lab: 'Lab technician',
  },

  userRoleLabel: 'Role',
  userActive: 'Active',
  userInactive: 'Inactive',
  walkIn: 'Walk-in customer',
  stockWarn: 'Not enough stock',
  saleNo: 'Sale No',
  saleReceiptTitle: 'Sale receipt',
  receiptTitle: 'Receipt',
  labReportTitle: 'Laboratory report',
  unit: 'units',
  currencyKip: 'LAK',

  fields: {
    patientName: 'Patient name',
    doctor: 'Doctor',
    date: 'Date',
    time: 'Time',
    type: 'Type',
    notes: 'Notes',
    service: 'Service',
    amount: 'Amount',
    itemName: 'Item name',
    qty: 'Quantity',
    price: 'Price',
    total: 'Total',
    age: 'Age',
    tel: 'Tel',
    occupation: 'Occupation',
    address: 'Address',
    diagnosis: 'Diagnosis',
    extNo: 'Ext No',
    itemsTitle: 'Items',
    addItem: 'Add row',
    cashier: 'Cashier',
    dob: 'Date of birth',
    treatingDoctor: 'Treating doctor',
    labNo: 'Lab No',
    extLabNo: 'Ext Lab No',
    extHn: 'Ext HN',
    ward: 'Ward',
    gender: 'Gender',
    analyte: 'Analyte',
    results: 'Results',
    unit: 'Unit',
    range: 'Normal Range',
    flag: 'Flag',
    reportedBy: 'Reported by',
    approveBy: 'Approved by',
    expiry: 'Expiry date',
    barcode: 'Drug code / Barcode',
    barcodeScan: 'Type or scan barcode, then press Enter',
    staff: 'Staff',
    currency: 'Currency',
  },

  currencies: {
    LAK: 'Kip (LAK)',
    THB: 'Baht (THB)',
    USD: 'Dollar (USD)',
  },

  gender: {
    female: 'Female',
    male: 'Male',
    other: 'Other',
  },

  patients: {
    searchPlaceholder: 'Search name, phone, code or Ext HN',
    code: 'Code',
    noResults: 'No matching patients',
    detailTitle: 'Patient record',
    tabInfo: 'Details',
    tabAppointments: 'Appointments',
    tabInvoices: 'Invoices',
    tabLab: 'Lab results',
    comingInPhase: 'Arrives in a later phase',
    editTitle: 'Edit patient',
    dobOrAge: 'Enter a date of birth or an age',
    extHnHint: 'Number from the previous system, if any',
    wardHint: 'Ward/department from the referring hospital, if any',
    saved: 'Saved',
    bloodType: 'Blood type',
    registeredAt: 'Registered on',
    cannotDelete: 'Cannot delete',
    cannotDeleteHasRecords: 'This patient has invoices or lab records and cannot be deleted.',
  },

  appointments: {
    bookTitle: 'Book appointment',
    duration: 'Duration (minutes)',
    noneToday: 'No appointments',
    conflictTitle: 'Doctor is already booked',
    conflictBody: 'This doctor already has an appointment in that slot. Book anyway?',
    bookAnyway: 'Book anyway',
    selectDoctor: 'Select doctor',
    noDoctor: 'Not assigned',
    changeStatus: 'Change status',
    booked: 'Appointment booked',
  },

  scanner: {
    needsHttps:
      'The camera only works over HTTPS. Type the code instead, or ask your administrator (see docs/setup.md §5)',
    permissionDenied: 'Camera permission denied — type the code instead',
    cameraFailed: 'Could not open the camera — type the code instead',
  },

  inventory: {
    searchPlaceholder: 'Search name, code or barcode',
    lowOnly: 'Low stock only',
    stockIn: 'Receive stock',
    stockInQty: 'Quantity received',
    unitCost: 'Cost per unit',
    newExpiry: 'New expiry date (optional)',
    reorderLevel: 'Reorder level',
    sellPrice: 'Sell price',
    costPrice: 'Cost price',
    stockOk: 'In stock',
    stockLow: 'Low stock',
    stockOut: 'Out of stock',
    expirySoon: 'Expiring soon',
    expired: 'Expired',
    noItems: 'Nothing in inventory yet',
    savedStockIn: 'Stock received',
    categories: 'Drug and supply categories',
    categoriesHint: 'These appear in the picker when adding or editing an item',
    categoryHint: 'Manage the list in Settings → Categories',
    newCategory: 'Add category',
    noCategories: 'No categories yet — add one before adding drugs',
    categoryTaken: 'That category already exists',
    categoryInUse: 'Cannot delete: items still use this category — retire it instead',
    renameWarning: 'Renaming also updates the {n} item(s) using this category',
    sortOrder: 'Order',
    itemsUsing: 'Items',
    sellBelowCostWarning: 'Sell price is below cost price — this item may sell at a loss',
    movementHistory: 'Movement history',
    movementType: 'Movement type',
    movementTypes: {
      in: 'Received',
      out: 'Written off',
      adjust: 'Adjusted',
      sale: 'Sold',
      return: 'Restocked',
    },
    received: 'Received',
    sold: 'Sold',
    returned: 'Restocked',
    adjusted: 'Adjusted',
    writtenOff: 'Written off',
    netChange: 'Net change',
  },

  pharmacy: {
    wholesale: 'Wholesale',
    wholesaleHint: '(price can be edited in the cart)',
    cart: 'Cart',
    cartEmpty: 'Nothing yet — scan or type a barcode to add an item',
    customer: 'Customer',
    checkout: 'Take payment',
    grandTotal: 'Total',
    notEnough: 'Not enough stock',
    notFound: 'No item matches that code',
    sold: 'Sale completed',
    history: 'Sale history',
    available: 'Available',
    paymentMethod: 'Payment method',
    remove: 'Remove',
    voidTitle: 'Void sale',
    voidReason: 'Reason for voiding',
    voidWarning: 'A voided sale restores every line to stock and cannot be undone',
    voided: 'Sale voided',
  },

  payment: {
    cash: 'Cash',
    transfer: 'Transfer',
    card: 'Card',
  },

  print: {
    backgroundHint:
      'If the table borders do not appear, enable "Background graphics" in the print dialog',
  },

  invoices: {
    searchPlaceholder: 'Search invoice no, Ext No or patient name',
    newTitle: 'New invoice',
    discount: 'Discount',
    subtotal: 'Subtotal',
    outstanding: 'Outstanding',
    addLine: 'Add row',
    fromService: 'From service',
    fromDrug: 'From inventory',
    pricePerLine: 'Price/line',
    custom: 'Custom',
    takePayment: 'Take payment',
    paymentAmount: 'Amount received',
    payFull: 'Pay in full',
    voidTitle: 'Void invoice',
    voidReason: 'Reason for voiding',
    voidWarning: 'A voided invoice can no longer take payment, but it is kept on record',
    noInvoices: 'No invoices yet',
    saved: 'Invoice issued',
    paymentSaved: 'Payment recorded',
    voided: 'Invoice voided',
    exceedsOutstanding: 'Amount exceeds the outstanding balance',
  },

  services: {
    title: 'Services',
    hint: 'The service list and default prices offered when issuing an invoice',
    newService: 'New service',
    code: 'Code',
    name: 'Service name',
    taken: 'That code already exists',
    noServices: 'No services yet',
  },

  lab: {
    newOrder: 'New lab order',
    selectTests: 'Select tests',
    selectedCount: 'Selected',
    noneSelected: 'No tests selected yet',
    enterResults: 'Enter results',
    hasValue: 'Has result',
    removeWithValue: 'This test already has a result. Unticking will delete it. Continue?',
    approve: 'Approve results',
    unapprove: 'Withdraw approval',
    approved: 'Approved',
    approvedLocked: 'Approved — no longer editable',
    extLabNo: 'Ext Lab No (from the outside laboratory)',
    noOrders: 'No lab orders yet',
    saved: 'Results saved',
    orderCreated: 'Lab order created',
    searchPlaceholder: 'Search Lab No',
    reportTitle: 'Laboratory report',
  },

  reports: {
    range: 'Date range',
    thisMonth: 'This month',
    last3Months: '3 months',
    last6Months: '6 months',
    thisYear: 'This year',
    billed: 'Billed',
    collected: 'Collected',
    patientsBilled: 'Patients billed',
    serviceMixNote: 'Based on amounts billed, not amounts collected',
    noData: 'No data in this range',
    totalLabel: 'Total',
    totalCost: 'Total cost',
    totalProfit: 'Total profit',
    profitMargin: 'Profit margin',
    sourceInvoice: 'Invoice',
    sourceSale: 'Pharmacy sale',
    cost: 'Cost',
    profit: 'Profit',
    profitHint: "Uses each item's current cost price, not the cost price on the day it sold",
  },

  settingsPage: {
    tabPersonal: 'Personal',
    tabClinic: 'Clinic details',
    tabSystem: 'System',
    tabUsers: 'Users',
    tabCategories: 'Categories',
    tabServices: 'Services',
    tabMenuAccess: 'Menu access',
    menuAccessHint:
      'Choose which sidebar items each role sees. This only controls what shows in the menu — actual data access still follows the original design.',
    logo: 'Clinic logo',
    logoHint: 'Image up to 512 KB — used on screen and on printed documents',
    logoTooLarge: 'File is larger than 512 KB',
    roundingUnit: 'Round invoice totals to (LAK)',
    expiryWarnDays: 'Warn before expiry (days)',
    fxThb: 'Baht exchange rate (LAK per 1 THB)',
    fxUsd: 'Dollar exchange rate (LAK per 1 USD)',
    fxHint:
      'Used to show the total in baht/dollars on the invoice and pharmacy screens — the ledger amount stays in kip either way',
    sessionTtlHours: 'Session timeout (hours)',
    sessionTtlHint:
      'Signs a user out automatically after this long with no activity (default 24 hours)',
    sessionExpiredTitle: 'Session ended',
    sessionExpiredBody: 'No activity for a while, or the session was ended — please sign in again',
    defaultLang: 'Default language',
    defaultTheme: 'Default colour theme',
    defaultMode: 'Default appearance',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordMin: 'At least 10 characters',
    passwordChanged: 'Password changed',
    passwordWrong: 'Current password is incorrect',
    newUser: 'Add user',
    username: 'Username',
    fullName: 'Full name',
    resetPassword: 'Reset password',
    resetWarning: 'This signs that user out of every device immediately',
    deactivate: 'Deactivate',
    activate: 'Activate',
    lastLogin: 'Last sign-in',
    usernameTaken: 'That username is already taken',
    lastAdmin: 'At least one active administrator must remain',
    selfDeactivate: 'You cannot deactivate your own account',
    saved: 'Saved',
    adminOnly: 'Administrators only',
  },

  common: {
    loading: 'Loading...',
    error: 'Something went wrong',
    noData: 'No data yet',
    noDataSub: 'Records will appear here once they exist',
    confirmDiscard: 'You have unsaved changes. Close anyway?',
    confirmStatusChange: 'Change this status?',
    confirmDelete: 'Delete this record? This cannot be undone.',
    confirmVoid: 'Confirm void',
    deleted: 'Deleted',
    of: 'of',
    page: 'Page',
    prev: 'Previous',
    next: 'Next',
    notFound: 'Page not found',
    backHome: 'Back to start',
    offline: 'No network connection — nothing can be saved until it returns',
    sessionExpired: 'Your session has ended — please sign in again',
  },

  errors: {
    UNAUTHENTICATED: 'Please sign in again',
    INVALID_CREDENTIALS: 'Incorrect username or password',
    FORBIDDEN: 'You do not have access to this area',
    NOT_FOUND: 'The requested record was not found',
    VALIDATION_FAILED: 'Some fields are not valid',
    CONFLICT: 'This record changed elsewhere — please reload',
    INSUFFICIENT_STOCK: 'Not enough stock',
    RATE_LIMITED: 'Too many attempts — please wait',
    INTERNAL: 'Server error — please try again',
  },
};
